import { DfiAnnualReport, DfiReportResponse, TqEnAnnualReportResponse } from 'types';

import { formatKpiData } from './formatKpiData';

import {
  useAgreedExtensionStatsLabels,
  useEiaConsultationStatsLabels,
  useNonStatutoryTimelinessResponseStatsLabels,
  useStatutoryTimeframeExceededStatsLabels,
  useStatutoryTimelinessResponseStatsLabels,
} from '../';
import { useThemedValueSwitch } from '@tq/ui-components/dist/hooks';
import { TFunction } from 'i18next';
import { northernIrelandTitles } from './northernIrelandTitles';

export const useNorthernIrelandFormattedDfiReport = (
  reportResponse: DfiReportResponse | TqEnAnnualReportResponse,
  t: TFunction
): {
  formattedReport: DfiAnnualReport | undefined;
  metrics: Record<string, unknown> | undefined;
  titles: Record<string, string> | undefined;
} => {
  const agreedExtensionStatsLabels = useAgreedExtensionStatsLabels();

  const eiaConsultationStatsLabels = useEiaConsultationStatsLabels();

  const nonStatutoryTimelinessResponseStatsLabels =
    useNonStatutoryTimelinessResponseStatsLabels();

  const statutoryTimeframeExceededStatsLabels = useStatutoryTimeframeExceededStatsLabels();

  const statutoryTimelinessResponseStatsLabels = useStatutoryTimelinessResponseStatsLabels();

  let formattedReport = undefined;
  let formattedMetrics = undefined;

  const { isNI } = useThemedValueSwitch();

  if (isNI && reportResponse && 'statutoryTimelinessResponseStats' in reportResponse) {
    formattedReport = {
      ...reportResponse,
      statutoryTimelinessResponseStats: formatKpiData(
        reportResponse.statutoryTimelinessResponseStats,
        statutoryTimelinessResponseStatsLabels
      ),
      lateResponseStatItems: reportResponse.lateResponseStatItems,
      statutoryTimeframeExceededStats: formatKpiData(
        reportResponse.statutoryTimeframeExceededStats,
        statutoryTimeframeExceededStatsLabels
      ),
      agreedExtensionStats: formatKpiData(
        reportResponse.agreedExtensionStats,
        agreedExtensionStatsLabels
      ),
      outstandingResponseStats: formatKpiData(
        {
          totalConsultationsWithOutstandingResponses:
            reportResponse.statutoryTimelinessResponseStats
              .totalConsultationsWithOutstandingResponses,
        },
        statutoryTimelinessResponseStatsLabels
      ),
      totalEiaConsultationsReceived: formatKpiData(
        {
          totalEiaConsultationsReceived:
            reportResponse.eiaConsultationStats.totalEiaConsultationsReceived,
        },
        eiaConsultationStatsLabels
      ),
      averageDaysTakenToRespondToEiaConsultations: formatKpiData(
        {
          averageDaysTakenToRespondToEiaConsultations:
            reportResponse.eiaConsultationStats.averageDaysTakenToRespondToEiaConsultations,
        },
        eiaConsultationStatsLabels
      ),
      nonStatutoryTimelinessResponseStats: formatKpiData(
        reportResponse.nonStatutoryTimelinessResponseStats,
        nonStatutoryTimelinessResponseStatsLabels
      ),
    };

    formattedMetrics = {
      statutoryTimelinessResponseStats: formattedReport.statutoryTimelinessResponseStats,
      lateResponseStatItems: formattedReport.lateResponseStatItems,
      statutoryTimeframeExceededStats: formattedReport.statutoryTimeframeExceededStats,
      agreedExtensionStats: formattedReport.agreedExtensionStats,
      outstandingResponseStats: formattedReport.outstandingResponseStats,
      totalEiaConsultationsReceived: formattedReport.totalEiaConsultationsReceived,
      averageDaysTakenToRespondToEiaConsultations:
        formattedReport.averageDaysTakenToRespondToEiaConsultations,
      nonStatutoryTimelinessResponseStats: formattedReport.nonStatutoryTimelinessResponseStats,
    };
  }

  return {
    formattedReport: formattedReport,
    metrics: formattedMetrics,
    titles: northernIrelandTitles(t),
  };
};
